import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import ReachOutSection from './ReachOutSection';

const HamburgerMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, language } = useLanguage();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <div className="hamburger-menu" ref={menuRef}>
      <button 
        className={`hamburger-button ${isOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={isOpen}
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>
      
      <div className={`menu-overlay ${isOpen ? 'open' : ''}`} onClick={closeMenu}>
        <div className="menu-content" onClick={(e) => e.stopPropagation()}>
          <div className="menu-header">
            <h3>{t('menu.title') || 'Menu'}</h3>
            <button 
              className="close-button"
              onClick={toggleMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          
          <nav className="menu-navigation">
            <Link to={`/${language}`} className={`menu-link ${location.pathname === `/${language}` || location.pathname === '/' ? 'active' : ''}`}>
              <svg className="menu-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </Link>
            <Link to={`/${language}/faq`} className={`menu-link ${location.pathname === `/${language}/faq` ? 'active' : ''}`}>
              FAQ
            </Link>
            <Link to={`/${language}/mapper`} className={`menu-link ${location.pathname === `/${language}/mapper` ? 'active' : ''}`}>
              {t('menu.mapper')}
            </Link>
            <Link to={`/${language}/submit-summit`} className={`menu-link ${location.pathname === `/${language}/submit-summit` ? 'active' : ''}`}>
              {t('common.submitSummit')}
            </Link>
            <Link to={`/${language}/privacy`} className={`menu-link ${location.pathname === `/${language}/privacy` ? 'active' : ''}`}>
              {t('common.privacyPolicy')}
            </Link>
          </nav>
          
          <div className="menu-download-section">
            <h3 className="menu-download-title">{t('menu.download') || 'Download ColQuest'}</h3>
            <div className="menu-download-buttons">
              <a 
                href="https://apps.apple.com/fr/app/colquest/id6743229570" 
                className="menu-store-button"
                target="_blank"
                rel="noopener noreferrer"
                data-umami-event="outbound-link-click"
                data-umami-event-url="https://apps.apple.com/fr/app/colquest/id6743229570"
              >
                <svg className="menu-store-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="menu-store-label">App Store</span>
              </a>
              
              <a 
                href="https://play.google.com/store/apps/details?id=com.colquest.app" 
                className="menu-store-button"
                target="_blank"
                rel="noopener noreferrer"
                data-umami-event="outbound-link-click"
                data-umami-event-url="https://play.google.com/store/apps/details?id=com.colquest.app"
              >
                <svg className="menu-store-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <span className="menu-store-label">Play Store</span>
              </a>
            </div>
          </div>
          
          <div className="menu-reach-out-section">
            <h3 className="menu-reach-out-title">{t('menu.reachOut') || 'Reach Out'}</h3>
            <ReachOutSection variant="menu" />
          </div>
          
          <div className="menu-language-switcher">
            <h3 className="menu-language-title">{t('menu.language') || 'Language'}</h3>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HamburgerMenu; 